<template>
  <div
    :class="['email-component', 'footer-component', { selected }]"
    :style="computedStyles"
    @click="handleClick"
  >
    <div class="footer-content">
      <div class="footer-layout">
        <!-- Company Logo Section (Top) -->
        <div v-if="componentData.showLogo" class="footer-logo">
          <img
            v-if="getLogoSource()"
            :src="getLogoSource()"
            :alt="componentData.logoAlt || 'Company Logo'"
            :style="logoStyles"
            class="footer-logo-image"
            @error="handleImageError"
          />
          <div v-else class="logo-placeholder" @click.stop="openImageUpload">
            <ImageEditor
              :edit="false"
              text="Add Logo"
              size="sm"
              :show_slider="false"
              :show-background-color="false"
              :show-description="false"
              :show-fit="false"
              @saveImage="handleImageUpload"
              @clickUpload="onImageUploadClick"
            />
          </div>
        </div>

        <!-- Social Media Links Section -->
        <div v-if="componentData.showSocialLinks" class="social-links">
          <a
            v-for="social in activeSocialLinks"
            :key="social.platform"
            :href="social.url"
            class="social-link"
            :title="social.platform"
            :style="socialLinkStyles"
          >
            <i v-if="social.platform !== 'x'" :class="getSocialIcon(social.platform)"></i>
            <span v-else class="x-icon"></span>
          </a>
        </div>

        <!-- Address Section -->
        <div v-if="componentData.addressDisplay !== 'none'" class="footer-address" :style="addressStyles">
          <span v-if="componentData.addressDisplay === 'company'">
            {{companyAddress}}
          </span>
          <span v-else-if="componentData.addressDisplay === 'group'">
            {{companyGroupAddress}}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ImageEditor from '@/components/ImageEditor'

export default {
  name: 'FooterComponent',
  components: {
    ImageEditor
  },
  props: {
    componentData: {
      type: Object,
      required: true
    },
    selected: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    computedStyles() {
      const styles = this.componentData.styles || {}
      return {
        backgroundColor: styles.backgroundColor || '#f8f9fa',
        width: '100%',
        margin: '0',
        padding: `${styles.paddingTop || '20'}px ${styles.paddingLeft || '20'}px ${styles.paddingBottom || '20'}px ${styles.paddingRight || '20'}px`,
        minHeight: styles.height || '80px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        position: 'relative',
        borderTop: styles.showTopBorder ? `1px solid ${styles.borderColor || '#e5e7eb'}` : 'none'
      }
    },

    logoStyles() {
      const styles = this.componentData.styles || {}
      return {
        maxWidth: `${styles.logoWidth || '120'}px`,
        height: 'auto',
        display: 'block'
      }
    },

    addressStyles() {
      const styles = this.componentData.styles || {}
      return {
        fontFamily: styles.fontFamily || 'Arial, sans-serif',
        fontSize: `${styles.fontSize || '13'}px`,
        color: styles.textColor || '#666666',
        lineHeight: '1.4',
        textAlign: 'center'
      }
    },

    socialLinkStyles() {
      const styles = this.componentData.styles || {}
      return {
        backgroundColor: styles.socialBackgroundColor || '#333333',
        color: styles.socialIconColor || '#ffffff'
      }
    },

    activeSocialLinks() {
      if (!this.componentData.socialLinks) return []
      return this.componentData.socialLinks.filter(link => link.url && link.url.trim())
    },

    companyAddress() {
      // This will be populated by backend
      return '{{company.address}}'
    },

    companyGroupAddress() {
      // This will be populated by backend
      return '{{company_group.address}}'
    }
  },
  methods: {
    handleClick() {
      this.$emit('click')
    },

    getLogoSource() {
      // Use uploaded S3 URL if available, fallback to logoSrc
      return this.componentData.uploadedUrl || this.componentData.logoSrc || ''
    },

    handleImageError(event) {
      console.error('Footer logo failed to load')
    },

    getCurrentImageData() {
      return this.componentData.imageEditorData || {
        url: this.componentData.uploadedUrl || this.componentData.logoSrc,
        description: this.componentData.logoAlt || '',
        scale: 100,
        background_color: '#e8e7ec',
        object_fit: 'cover',
        x: 0,
        y: 0,
        image_id: this.componentData.imageMetadata?.imageId || ''
      }
    },

    handleImageUpload(imageData) {
      this.$set(this.componentData, 'uploadedUrl', imageData.url)
      this.$set(this.componentData, 'sourceType', 'upload')

      this.$set(this.componentData, 'imageEditorData', {
        url: imageData.url,
        description: imageData.description || '',
        scale: imageData.scale || 100,
        background_color: imageData.background_color || '#e8e7ec',
        object_fit: imageData.object_fit || 'cover',
        x: imageData.x || 0,
        y: imageData.y || 0,
        image_id: imageData.image_id || ''
      })

      if (imageData.description) {
        this.$set(this.componentData, 'logoAlt', imageData.description)
      }

      this.$set(this.componentData, 'imageMetadata', {
        uploadedAt: new Date().toISOString(),
        originalFile: imageData.file || null,
        imageId: imageData.image_id || null
      })

      this.$emit('update')

      this.$notify({
        title: 'Logo Uploaded',
        message: 'Your footer logo has been uploaded successfully',
        type: 'success',
        duration: 3000
      })
    },

    openImageUpload() {
      console.log('Opening image upload for footer')
    },

    onImageUploadClick() {
      console.log('Footer image upload initiated')
    },

    getSocialIcon(platform) {
      const icons = {
        facebook: 'fab fa-facebook-f',
        x: 'fab fa-twitter', // Using classic Twitter icon as fallback
        instagram: 'fab fa-instagram',
        linkedin: 'fab fa-linkedin-in',
        youtube: 'fab fa-youtube',
        tripadvisor: 'fab fa-tripadvisor',
        yelp: 'fab fa-yelp',
        morty: 'tim-icons icon-app',
        website: 'tim-icons icon-world'
      }
      return icons[platform] || 'tim-icons icon-link-72'
    }
  }
}
</script>

<style scoped>
.footer-component {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  border-radius: 0;
  margin: 0 !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  position: relative;
}

.footer-component:hover {
  border-color: #e14eca;
  border-bottom-color: transparent;
  box-shadow: 0 -4px 8px rgba(225, 78, 202, 0.1);
}

.footer-component.selected {
  border-color: #e14eca;
  border-bottom-color: transparent !important;
  box-shadow: 0 -4px 8px rgba(225, 78, 202, 0.1);
}

.footer-content {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.footer-layout {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 600px;
  gap: 1rem;
  text-align: center;
}

/* Logo Section */
.footer-logo {
  flex-shrink: 0;
}

.footer-logo-image {
  display: block;
  max-height: 100px;
}

.logo-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  border: 2px dashed rgba(0, 0, 0, 0.2);
  border-radius: 0.375rem;
  transition: all 0.2s;
}

.logo-placeholder:hover {
  border-color: rgba(0, 0, 0, 0.4);
  background: rgba(0, 0, 0, 0.05);
}

/* Address Section */
.footer-address {
  width: 100%;
  max-width: 500px;
}

/* Social Links */
.social-links {
  display: flex;
  gap: 0.75rem;
  flex-shrink: 0;
}

.social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  text-decoration: none;
  transition: all 0.2s;
  font-size: 14px;
}

.social-link:hover {
  transform: translateY(-1px);
  opacity: 0.8;
}

/* Custom X icon styling */
.x-icon {
  font-style: normal !important;
  font-weight: bold;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}
.x-icon:before {
  content: "𝕏";
  font-size: 16px;
  line-height: 1;
  display: inline-block;
}

/* Responsive Design */
@media (max-width: 768px) {
  .footer-layout {
    gap: 0.75rem;
  }

  .footer-logo-image {
    max-height: 50px;
  }
}

@media (max-width: 480px) {
  .footer-layout {
    gap: 0.5rem;
  }

  .social-links {
    gap: 0.5rem;
  }

  .social-link {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }

  .footer-address {
    font-size: 11px;
  }

  .footer-logo-image {
    max-height: 40px;
  }
}
</style>
